import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "admin/AdminLayout";
import StatCard from "admin/StatCard";
import ProductFormModal from "admin/ProductFormModal";
import ConfirmDialog from "admin/ConfirmDialog";
import { listProducts, deleteProduct, usingDemoStorage } from "lib/db";

export default function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [modalState, setModalState] = useState({ open: false, product: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const refresh = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const data = await listProducts();
      setProducts(data);
    } catch (err) {
      setLoadError(err.message || "Could not load products.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.type))).sort(),
    [products],
  );

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch = !q || p.title.toLowerCase().includes(q);
      const matchesCategory = categoryFilter === "All" || p.type === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, categoryFilter]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteProduct(deleteTarget.id);
      toast.success(`"${deleteTarget.title}" deleted.`);
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not delete product.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <ToastContainer position="bottom-right" />

      {usingDemoStorage && (
        <div className="mb-6 rounded-xl border border-gold-200 bg-gold-50 px-4 py-3 text-sm text-gold-900">
          <strong className="font-semibold">Demo mode:</strong> product changes are saved in this browser only.
          Connect Supabase (see <code className="rounded bg-white/60 px-1">DEPLOYMENT.md</code>) to make this the
          live, permanent catalog.
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-800 sm:text-3xl">Products</h1>
          <p className="mt-1 text-sm text-slate-500">Add, edit, or remove products shown on the website.</p>
        </div>
        <button
          type="button"
          onClick={() => setModalState({ open: true, product: null })}
          className="flex items-center gap-2 rounded-lg bg-maroon-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-maroon-800"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Product
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total Products" value={products.length} accent />
        <StatCard label="Categories" value={categories.length} />
        <StatCard
          label="Missing Images"
          value={products.filter((p) => !p.image_url).length}
        />
        <StatCard label="Showing" value={filteredProducts.length} />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-xs">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
        >
          <option value="All">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3 font-medium">Image</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Packaging</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading
              && Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-4 py-3" colSpan={5}>
                    <div className="h-10 animate-pulse rounded bg-slate-100" />
                  </td>
                </tr>
              ))}

            {!isLoading && loadError && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-red-600">
                  {loadError}
                </td>
              </tr>
            )}

            {!isLoading && !loadError && filteredProducts.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                  {products.length === 0
                    ? "No products yet — click “Add Product” to create your first one."
                    : "No products match your search."}
                </td>
              </tr>
            )}

            {!isLoading
              && !loadError
              && filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.title}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <svg className="h-5 w-5 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">{product.title}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-maroon-50 px-2.5 py-1 text-xs font-medium text-maroon-700">
                      {product.type}
                    </span>
                  </td>
                  <td className="max-w-xs px-4 py-3 text-slate-500">
                    {product.packaging?.length ? product.packaging.join(", ") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setModalState({ open: true, product })}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-maroon-200 hover:bg-maroon-50 hover:text-maroon-700"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(product)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <ProductFormModal
        open={modalState.open}
        product={modalState.product}
        onClose={() => setModalState({ open: false, product: null })}
        onSaved={() => {
          setModalState({ open: false, product: null });
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.title}"?`}
        description="This removes the product and its image from the catalog. This can't be undone."
        confirmLabel="Delete"
        danger
        isBusy={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </AdminLayout>
  );
}
